// The cart builder's "Change an email subscription" section: sell a tier
// upgrade or an add-on on a Microsoft 365 subscription the account already has.
//
// The lines come from squish-backend (lib/staff-email-change.js
// staffEmailChangeOptions, through squish-crm-backend's
// /api/accounts/:accountId/email-subscriptions/:subId/cart-options), priced by
// the same quote functions as the customer's dashboard. This file only picks
// a served line and keeps the cart to what checkout will accept:
//
//   - one upgrade per subscription (a second pick replaces the first),
//   - one line per add-on per subscription (a new seat count replaces it),
//   - never an upgrade AND an add-on on the same subscription. Checkout
//     refuses that mix: the add-on would be ordered against the tier the
//     upgrade is replacing. Nor an upgrade beside more seats on it
//     (src/lib/emailSeats.js), for the same reason.
//
// An upgrade's price is what is due today: the new tier's period, less a
// credit for the unused part of the current one. The credit runs down every
// day, so due today grows, and checkout refuses a cart whose price has moved
// past its stale-cart tolerance (the larger of 50 cents and 1%). A link paid a
// day or more after it was built is refused and needs a fresh cart.

import { upgradeBesideSeatsBlocker } from './emailSeats.js';

const UPGRADE = 'EMAIL_UPGRADE';
const ADDON = 'EMAIL_ADDON';

export const isEmailChangeLine = (i) => i?.productCode === UPGRADE || i?.productCode === ADDON;

// The subscription a line changes.
export function subscriptionOfLine(i) {
  if (i?.productCode === UPGRADE) return i.metadata?.subscriptionId || null;
  if (i?.productCode === ADDON) return i.metadata?.parentSubscriptionId || null;
  return null;
}

export function emailChangeLineId(line) {
  const sub = subscriptionOfLine(line);
  return line.productCode === UPGRADE ? `email-upgrade:${sub}` : `email-addon:${sub}:${line.metadata?.sku}`;
}

// The account's subscriptions this section can change, from its products
// (listAccountProducts). The email adapter lists an `upgrade` action only on a
// live subscription below the top tier, and `add_addon` only on a live one not
// scheduled to cancel; a subscription with neither is left out.
export function changeableEmailSubs(products) {
  return (Array.isArray(products) ? products : [])
    .filter((p) => p?.productKind === 'email' && p.productId)
    .map((p) => {
      const kinds = new Set((p.actions || []).map((a) => a?.kind));
      return {
        subscriptionId: String(p.productId),
        label: p.label || p.sku || String(p.productId),
        domain: p.linkedDomain || null,
        canUpgrade: kinds.has('upgrade'),
        canAddon: kinds.has('add_addon'),
      };
    })
    .filter((s) => s.canUpgrade || s.canAddon);
}

// Why an add-on can't be bought on this subscription, or null when it can.
export function addonUnavailableReason(o) {
  if (!o || o.available) return null;
  if (o.alreadyOwned) return 'already on this subscription';
  if (o.includedInTier) return 'included in their tier';
  if (o.annualOnlyBlocked) return 'annual plans only';
  if (o.baseTierBlocked) return o.baseTierBlockedReason || `needs ${o.requiresBaseTierLabel || 'a higher tier'}`;
  return 'not available';
}

// Add one served line. Replaces the same upgrade / add-on already in the cart
// (an upgrade's id is its subscription's, so a new tier replaces the old one)
// and keeps everything else in order.
export function withEmailChange(items, line) {
  const lineId = emailChangeLineId(line);
  return [...(items || []).filter((i) => i.lineId !== lineId), { ...line, lineId }];
}

// Why the rep can't add this line right now (null = addable).
//   subs: null while loading, { error } when the account's products failed,
//         otherwise the array from changeableEmailSubs.
export function emailChangeBlocker({ isInvoice, isProspect, subs, items, line }) {
  if (isProspect) return 'An upgrade or add-on changes a subscription the customer already has, so it needs their account';
  if (isInvoice) return 'Card checkout only: switch to Card to sell an upgrade or add-on';
  if (subs == null) return 'Loading this account\'s email subscriptions…';
  if (subs.error) return `Couldn't load this account's email subscriptions: ${subs.error}`;
  if (subs.length === 0) return 'No live email subscription on this account to upgrade or add to.';
  if (!line) return null;
  const sub = subscriptionOfLine(line);
  if (line.productCode === UPGRADE) {
    const seats = upgradeBesideSeatsBlocker(items, sub);
    if (seats) return seats;
  }
  const other = (items || []).find((i) => isEmailChangeLine(i) && subscriptionOfLine(i) === sub
    && i.productCode !== line.productCode);
  if (other) {
    return line.productCode === UPGRADE
      ? 'This cart already adds an add-on to this subscription. Sell the upgrade on its own cart first.'
      : 'This cart already upgrades this subscription. Sell the add-on after the upgrade, on the new tier.';
  }
  return null;
}

// Plain-language price for an upgrade quote: what is due today and why.
export function upgradeSummary(q) {
  const d = (c) => `$${(c / 100).toFixed(2)}`;
  if (!q?.eligible) return q?.reason || 'not available';
  const credit = q.creditCents > 0 ? `, after ${d(q.creditCents)} credit for unused time` : '';
  return `${d(q.dueTodayCents)} today${credit}, then ${d(q.recurringCents)}/${q.billingTerm === 'annual' ? 'yr' : 'mo'}`;
}
